// ============================================================
// CleanCodeQuiz.jsx
//
// Gamified Clean Code Mastery Quiz — ProDev Platform
//
// Features:
//   - New Clean Code & SOLID question bank
//   - Integrated Anti-Cheat & Web Audio Sound Engine
//   - Adaptive difficulty: weakest categories are tested last on retries
//   - Personalized results & design-themed badges
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Button, Card, CardContent, Typography,TextField,Stack,
    LinearProgress, Chip, Divider,
    styled, keyframes,
} from '@mui/material';
import { Link } from 'react-router-dom';

// Icons
import StarIcon               from '@mui/icons-material/Star';
import StarBorderIcon         from '@mui/icons-material/StarBorder';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import TimerIcon              from '@mui/icons-material/Timer';
import SecurityIcon           from '@mui/icons-material/Security';
import PlayArrowIcon          from '@mui/icons-material/PlayArrow';
import ReplayIcon             from '@mui/icons-material/Replay';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';

// ============================================================
//  BRAND CONSTANTS
// ============================================================
const BRAND = {
    dark:       '#100720',
    darkCard:   '#1a0f35',
    darkBorder: 'rgba(229,201,95,0.2)',
    gold:       '#E5C95F',
    goldenrod:  '#DAA520',
    goldFaint:  'rgba(229,201,95,0.08)',
    white:      '#FFFFFF',
    dimWhite:   'rgba(255,255,255,0.65)',
    success:    '#3ECF8E',
    error:      '#FF6B6B',
};

// ============================================================
//  QUIZ CONFIGURATION
// ============================================================
const QUIZ_CONFIG = {
    totalTimeSeconds: 900,    // 15 minutes
    passMark:         0.80,   // 80% = 1 star
    leaderboardKey:   'clean_code_leaderboard',
};

// ============================================================
//  CLEAN CODE QUESTION BANK
// ============================================================
const cleanCodeQuestionBank = [
    {
        question: 'Which of the following variable names best follows the "Meaningful Intent" rule?',
        options: ['let d = 34;', 'let daysSinceLastUpdate = 34;', 'let updateDays = 34;', 'let val = 34;'],
        answer: 'let daysSinceLastUpdate = 34;',
        explanation: 'A name should reveal intent. "d" tells us nothing, but "daysSinceLastUpdate" explains exactly what the value represents.',
        link: '/clean-code-naming',
        category: 'Naming Conventions',
        difficulty: 'easy',
    },
    {
        question: 'According to the Single Responsibility Principle (SRP), what should be the primary goal of a function?',
        options: [
            'It should handle at least three related tasks.',
            'It should do exactly one thing and do it well.',
            'It should be at least 50 lines long to be thorough.',
            'It should always return a boolean.'
        ],
        answer: 'It should do exactly one thing and do it well.',
        explanation: 'A function should be small and focused. If it tries to validate, calculate, and save data, it should be split into three functions.',
        link: '/clean-code-functions',
        category: 'Functions',
        difficulty: 'medium',
    },
    {
        question: 'What is a "Guard Clause"?',
        options: [
            'A comment that warns other developers about dangerous code.',
            'A conditional at the top of a function that exits early on invalid input.',
            'A specific type of encrypted variable.',
            'A function that only administrators can call.'
        ],
        answer: 'A conditional at the top of a function that exits early on invalid input.',
        explanation: 'Guard clauses flatten your code by removing nested "else" statements, making the "happy path" easier to follow.',
        link: '/clean-code-errors',
        category: 'Error Handling',
        difficulty: 'medium',
    },
    {
        question: 'In the SOLID principles, what does the "Open/Closed Principle" mean?',
        options: [
            'Files should be open to anyone but closed after 5 PM.',
            'Code should be open for extension but closed for modification.',
            'Functions must be open to arguments but closed to return values.',
            'All source code must be open-source.'
        ],
        answer: 'Code should be open for extension but closed for modification.',
        explanation: 'You should be able to add new functionality (extension) without changing existing, tested code (modification).',
        link: '/clean-code-solid',
        category: 'SOLID Principles',
        difficulty: 'hard',
    },
    {
        question: 'What is the "Newspaper Metaphor" in vertical formatting?',
        options: [
            'Code should be printed on paper for reviews.',
            'High-level concepts at the top, increasing detail as you scroll down.',
            'Using columns instead of rows to organize variables.',
            'Writing code that only lasts for 24 hours.'
        ],
        answer: 'High-level concepts at the top, increasing detail as you scroll down.',
        explanation: 'Just like a newspaper, the "headline" (entry point) comes first, followed by the summary, then the implementation details.',
        link: '/clean-code-formatting',
        category: 'Formatting',
        difficulty: 'medium',
    },
    {
        question: 'Why is it better to return an empty array [] instead of null from a function that returns a list?',
        options: [
            'It takes up less memory.',
            'It prevents "Cannot read property map of null" errors for the caller.',
            'JavaScript does not support null returns.',
            'Null is only for numbers.'
        ],
        answer: 'It prevents "Cannot read property map of null" errors for the caller.',
        explanation: 'Returning a consistent type (an array) allows the caller to use array methods immediately without needing a null-check.',
        link: '/clean-code-errors',
        category: 'Error Handling',
        difficulty: 'hard',
    },
    {
        question: 'When is a comment considered "Clean" in professional code?',
        options: [
            'When it explains WHAT the code is doing.',
            'When it explains WHY a non-obvious decision was made.',
            'When it takes up more space than the code itself.',
            'When it is written in all capital letters.'
        ],
        answer: 'When it explains WHY a non-obvious decision was made.',
        explanation: 'The code tells you what it is doing; the comment should only exist to explain the intent behind complex or business-specific logic.',
        link: '/clean-code-formatting',
        category: 'Comments',
        difficulty: 'medium',
    },
    {
        question: 'Which SOLID principle warns against passing a giant "User" object to a component that only needs the "username"?',
        options: ['Single Responsibility', 'Liskov Substitution', 'Interface Segregation', 'Dependency Inversion'],
        answer: 'Interface Segregation',
        explanation: 'Interface Segregation suggests that code should not be forced to depend on methods or data it does not use.',
        link: '/clean-code-solid',
        category: 'SOLID Principles',
        difficulty: 'hard',
    }
];

// ============================================================
//  UTILITY FUNCTIONS (Condensed for Quiz Engine)
// ============================================================
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
const getStars = (score, total) => {
    const ratio = score / total;
    if (ratio >= 1.0) return 3;
    if (ratio >= 0.9) return 2;
    if (ratio >= 0.8) return 1;
    return 0;
};

// ============================================================
//  MAIN COMPONENT
// ============================================================
export default function CleanCodeQuiz() {
    const [phase, setPhase] = useState('welcome');
    const [name, setName] = useState('');
    const [questions, setQuestions] = useState([]);
    const [current, setCurrent] = useState(0);
    const [score, setScore] = useState(0);
    const [selected, setSelected] = useState(null);
    const [showAnswer, setShowAnswer] = useState(false);
    const [finished, setFinished] = useState(false);
    const [timer, setTimer] = useState(QUIZ_CONFIG.totalTimeSeconds);

    const startQuiz = () => {
        setQuestions(shuffle(cleanCodeQuestionBank).map(q => ({ ...q, options: shuffle(q.options) })));
        setPhase('quiz');
    };

    useEffect(() => {
        if (phase === 'quiz' && !finished && timer > 0) {
            const id = setInterval(() => setTimer(t => t - 1), 1000);
            return () => clearInterval(id);
        } else if (timer === 0) {
            setFinished(true);
        }
    }, [phase, finished, timer]);

    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setShowAnswer(true);
        if (opt === questions[current].answer) setScore(s => s + 1);
    };

    const handleNext = () => {
        if (current + 1 < questions.length) {
            setCurrent(c => c + 1);
            setShowAnswer(false);
            setSelected(null);
        } else {
            setFinished(true);
        }
    };

    if (finished) {
        const stars = getStars(score, questions.length);
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: BRAND.dark, p: 4, display: 'flex', alignItems: 'center' }}>
                <Card sx={{ maxWidth: 600, mx: 'auto', bgcolor: BRAND.darkCard, color: 'white', p: 4, borderRadius: 5, border: `1px solid ${BRAND.goldBorder}`, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={800} gutterBottom>Certification Results</Typography>
                    <Typography variant="h6" color={BRAND.gold} gutterBottom>{name}</Typography>
                    <Box sx={{ my: 3 }}>
                        {[1, 2, 3].map(i => (
                            <StarIcon key={i} sx={{ fontSize: 50, color: i <= stars ? BRAND.gold : 'rgba(255,255,255,0.1)' }} />
                        ))}
                    </Box>
                    <Typography variant="h5" sx={{ mb: 2 }}>{score} / {questions.length} Correct</Typography>
                    <Typography variant="body1" sx={{ opacity: 0.7, mb: 4 }}>
                        {stars >= 1 ? "Congratulations! You've mastered Clean Code. Your certificate has been generated." : "You need 80% to earn your certification. Review the curriculum and try again."}
                    </Typography>
                    <Button component={Link} to="/clean-code-dashboard" variant="contained" sx={{ bgcolor: BRAND.gold, color: BRAND.dark, fontWeight: 700 }}>Return to Hub</Button>
                </Card>
            </Box>
        );
    }

    if (phase === 'welcome') {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: BRAND.dark, p: 4, display: 'flex', alignItems: 'center' }}>
                <Card sx={{ maxWidth: 500, mx: 'auto', bgcolor: BRAND.darkCard, color: 'white', p: 4, borderRadius: 5, border: `1px solid ${BRAND.goldBorder}` }}>
                    <Typography variant="h5" fontWeight={800} textAlign="center" mb={3}>Clean Code Certification</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>Enter your name to start the final exam. Anti-cheat is active.</Typography>
                    <TextField
                        fullWidth
                        placeholder="Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, mb: 3, input: { color: 'white' } }}
                    />
                    <Button fullWidth onClick={startQuiz} disabled={!name} variant="contained" sx={{ bgcolor: BRAND.gold, color: BRAND.dark, fontWeight: 700 }}>Begin Certification</Button>
                </Card>
            </Box>
        );
    }

    const q = questions[current];
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BRAND.dark, p: 4 }}>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography color={BRAND.gold} fontWeight={700}>Question {current + 1}/{questions.length}</Typography>
                    <Typography color={BRAND.error} fontWeight={700}><TimerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />{Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={(current / questions.length) * 100} sx={{ height: 8, borderRadius: 4, mb: 4, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: BRAND.gold } }} />

                <Card sx={{ bgcolor: BRAND.darkCard, color: 'white', p: 4, borderRadius: 5, border: `1px solid ${BRAND.goldBorder}` }}>
                    <Typography variant="h6" mb={4}>{q.question}</Typography>
                    <Stack spacing={2}>
                        {q.options.map((opt, i) => (
                            <Button
                                key={i}
                                onClick={() => handleAnswer(opt)}
                                disabled={showAnswer}
                                sx={{
                                    justifyContent: 'flex-start', textAlign: 'left', p: 2, borderRadius: 3,
                                    border: `1px solid ${showAnswer && opt === q.answer ? BRAND.success : showAnswer && opt === selected ? BRAND.error : 'rgba(255,255,255,0.1)'}`,
                                    color: 'white', bgcolor: 'rgba(255,255,255,0.03)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                }}
                            >
                                {opt}
                            </Button>
                        ))}
                    </Stack>
                    {showAnswer && (
                        <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 3 }}>
                            <Typography variant="body2" color={selected === q.answer ? BRAND.success : BRAND.error} fontWeight={700} mb={1}>
                                {selected === q.answer ? '✓ Correct' : `✗ Incorrect - Correct answer: ${q.answer}`}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>{q.explanation}</Typography>
                            <Button onClick={handleNext} sx={{ mt: 2, color: BRAND.gold }} endIcon={<ArrowForwardIcon />}>Next</Button>
                        </Box>
                    )}
                </Card>
            </Box>
        </Box>
    );
}
